import { ExtractComponentProp } from '@approvalmax/types';
import { Button, Dropzone, Form, Popup, Text, toast, useExtensions } from '@approvalmax/ui/src/components';
import { FC, memo, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useUpdateXeroBudget, useUploadXeroBudget } from 'shared/data/webApp/v1';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { learnMoreUrl, sourceFileIdName, sourceFilesField } from './ReuploadPopupContent.constants';
import { messages } from './ReuploadPopupContent.messages';
import { FormValues, ReuploadPopupContentProps } from './ReuploadPopupContent.types';

const ReuploadPopupContent: FC<ReuploadPopupContentProps> = memo((props) => {
    const { budgetId, toggleIsOpenPopup } = props;

    const company = useCompanyBasedOnUrl();
    const extensions = useExtensions(['csv']);

    const {
        mutate: updateXeroBudget,
        isLoading: isLoadingUpdateXeroBudget,
        isSuccess: isSuccessUpdateXeroBudget,
    } = useUpdateXeroBudget();
    const { data: xeroBudget, mutate: uploadXeroBudget, isLoading: isLoadingUploadXeroBudget } = useUploadXeroBudget();

    const form = useForm<FormValues>({
        defaultValues: {
            companyId: company.id,
            budgetId,
            [sourceFilesField]: [],
        },
    });

    form.register(sourceFileIdName, { required: true });

    useEffect(() => {
        if (xeroBudget?.data.sourceFileId) {
            form.setValue(sourceFileIdName, xeroBudget.data.sourceFileId);
        }
    }, [form, xeroBudget?.data.sourceFileId]);

    const onSubmit = form.handleSubmit(({ sourceFiles, ...data }) => updateXeroBudget({ body: data }));

    const onDropAccepted = useCallback<ExtractComponentProp<typeof Dropzone, 'onDropAccepted'>>(
        (accepted) => {
            if (!accepted.length) return;

            const data = new FormData();

            data.append('file', accepted[0].source);
            uploadXeroBudget({ body: data });
        },
        [uploadXeroBudget]
    );

    const onRemoveFile = useCallback<ExtractComponentProp<typeof Dropzone, 'onRemove'>>(
        () => form.resetField('sourceFileId'),
        [form]
    );

    useEffect(() => {
        if (isSuccessUpdateXeroBudget) {
            toast.success(messages.editBudgetSuccessMessage);
            toggleIsOpenPopup(false);
        }
    }, [isSuccessUpdateXeroBudget, toggleIsOpenPopup]);

    return (
        <Form form={form} onSubmit={onSubmit}>
            <Popup.Header
                title={messages.title}
                actions={
                    <Button
                        size='medium'
                        color='blue80'
                        disabled={isLoadingUpdateXeroBudget || isLoadingUploadXeroBudget}
                        type='submit'
                    >
                        {messages.updateButton}
                    </Button>
                }
                progress={isLoadingUpdateXeroBudget || isLoadingUploadXeroBudget}
            />

            <Popup.Body>
                <Dropzone
                    name={sourceFilesField}
                    accept={extensions}
                    onDropAccepted={onDropAccepted}
                    onRemove={onRemoveFile}
                    hideZone
                    disabled={isLoadingUpdateXeroBudget || isLoadingUploadXeroBudget}
                    progress={isLoadingUploadXeroBudget}
                    invalid={Boolean(form.formState.errors.sourceFileId)}
                >
                    <Text font='body' spacing='0 0 24'>
                        {messages.description({
                            learnMore: (
                                <a href={learnMoreUrl} target='_blank' rel='noopener noreferrer'>
                                    {messages.learnMore}
                                </a>
                            ),
                            br: <br />,
                        })}
                    </Text>

                    <Text font='headline' fontSize='xxsmall' fontWeight='medium' spacing='0 0 16' as='h4'>
                        {messages.fileHeadline}
                    </Text>
                </Dropzone>
            </Popup.Body>
        </Form>
    );
});

export default ReuploadPopupContent;
